import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSeo } from '@/lib/seo';
import { useJsonLd, buildArticleSchema } from '@/lib/jsonld';

const ARTICLE_HEADER_IMAGE = 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20showing%20spreadsheet%20data%20analysis%20on%20clean%20white%20desk%2C%20soft%20natural%20lighting%2C%20neutral%20warm%20tones%2C%20editorial%20photography%2C%20professional%20aesthetic&width=1600&height=600&seq=article-header-bg-01&orientation=landscape';

const tocItems = [
  { id: 'what-is', labelKey: 'article.cssbuyGuide.whatIs' },
  { id: 'why-use', labelKey: 'article.cssbuyGuide.whyUse' },
  { id: 'how-to-find', labelKey: 'article.cssbuyGuide.howToFind' },
  { id: 'categories', labelKey: 'article.cssbuyGuide.categories' },
  { id: 'qc', labelKey: 'article.cssbuyGuide.qc' },
  { id: 'safety', labelKey: 'article.cssbuyGuide.safety' },
  { id: 'shipping', labelKey: 'article.cssbuyGuide.shipping' },
  { id: 'tips', labelKey: 'article.cssbuyGuide.tips' },
  { id: 'conclusion', labelKey: 'article.cssbuyGuide.conclusion' },
];

function TableOfContents({ t, activeId }: { t: (key: string) => string; activeId: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 sticky top-24">
      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
        {t('seo:article.cssbuyGuide.tableOfContents')}
      </h3>
      <ul className="space-y-2">
        {tocItems.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className={`block text-sm transition-colors ${
                activeId === item.id
                  ? 'text-emerald-700 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(`seo:${item.labelKey}`)}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ArticlePage() {
  const { t } = useTranslation(['common', 'seo']);
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('');

  useSeo({
    title: t('seo:article.cssbuyGuide.metaTitle'),
    description: t('seo:article.cssbuyGuide.metaDesc'),
    canonical: '/article/cssbuy-spreadsheet-guide',
    ogType: 'article',
    ogImage: ARTICLE_HEADER_IMAGE,
  });

  useJsonLd(
    buildArticleSchema({
      title: t('seo:article.cssbuyGuide.metaTitle'),
      description: t('seo:article.cssbuyGuide.metaDesc'),
      image: ARTICLE_HEADER_IMAGE,
      url: '/article/cssbuy-spreadsheet-guide',
    })
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );

    tocItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {t('common:breadcrumb.home')}
            </button>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
            <span className="text-gray-400">{t('common:breadcrumb.articles')}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ARTICLE_HEADER_IMAGE}
            alt="Article Header"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-medium">
                Guide
              </span>
              <span className="text-gray-300 text-sm">{t('common:article.cssbuyGuide.readTime')}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {t('seo:article.cssbuyGuide.h1')}
            </h1>
            <p className="text-gray-200 text-sm md:text-base leading-relaxed">
              {t('seo:article.cssbuyGuide.intro')}
            </p>
          </div>
        </div>
      </div>

      {/* Article Body */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-8">
            {/* Author info */}
            <div className="flex items-center gap-3 mb-10 pb-6 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm">
                C
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t('seo:article.cssbuyGuide.author')}</p>
                <p className="text-xs text-gray-400">
                  {t('seo:article.cssbuyGuide.published')}: {t('common:article.cssbuyGuide.date')} | {t('seo:article.cssbuyGuide.updated')}: {t('common:article.cssbuyGuide.date')}
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <p className="text-gray-600 leading-relaxed text-base">
                {t('seo:article.cssbuyGuide.intro')}
              </p>

              <section id="what-is" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.whatIs')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.whatIsDesc')}
                </p>
              </section>

              <section id="why-use" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.whyUse')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.whyUseDesc')}
                </p>
              </section>

              <section id="how-to-find" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.howToFind')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.howToFindDesc')}
                </p>
              </section>

              <section id="categories" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.categories')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.categoriesDesc')}
                </p>
              </section>

              <section id="qc" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.qc')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.qcDesc')}
                </p>
              </section>

              <section id="safety" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.safety')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.safetyDesc')}
                </p>
              </section>

              <section id="shipping" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.shipping')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.shippingDesc')}
                </p>
              </section>

              <section id="tips" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.tips')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.tipsDesc')}
                </p>
              </section>

              <section id="conclusion" className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {t('seo:article.cssbuyGuide.conclusion')}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t('seo:article.cssbuyGuide.conclusionDesc')}
                </p>
              </section>
            </div>

            {/* Internal link back to home */}
            <div className="mt-10 pt-8 border-t border-gray-100">
              <div className="bg-emerald-50 rounded-xl p-6 md:p-8">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('seo:article.cssbuyGuide.backToHome')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Explore more resources, categories, and guides on the CSSBuy Spreadsheet homepage.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer"
                >
                  {t('common:cta.explore')}
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('seo:article.cssbuyGuide.related')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/article/cssbuy-shipping-guide"
                  className="group block p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all"
                >
                  <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-700 transition-colors">
                    CSSBuy Shipping Guide: Complete Overview
                  </p>
                  <p className="text-xs text-gray-400 mt-1">8 min read</p>
                </Link>
                <Link
                  to="/article/cssbuy-qc-guide"
                  className="group block p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all"
                >
                  <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-700 transition-colors">
                    CSSBuy QC Guide: How to Check Quality
                  </p>
                  <p className="text-xs text-gray-400 mt-1">8 min read</p>
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <TableOfContents t={t} activeId={activeId} />

            {/* CTA Box */}
            <div className="mt-6 bg-gray-900 rounded-xl p-6 text-white">
              <h3 className="font-semibold mb-2">CSSBuy Spreadsheet</h3>
              <p className="text-sm text-gray-300 mb-4">
                Ready to start shopping? Visit the main website for the full product catalog.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                {t('common:cta.visitMain')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}